import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../utils/cn";
import { useOverlayLifecycle } from "../_shared/hooks/useOverlayLifecycle";
import { DSOverlayPortal } from "../_shared/ui/DSOverlayPortal";
import { DSOverlayRoot } from "../_shared/ui/DSOverlayRoot";

type DSDrawerOrientation = "bottom" | "left" | "right" | "top";
type DSDrawerSize = "fit" | "full";

type DSDrawerProps = {
  children: ReactNode;
  closeOnOverlayClick?: boolean;
  open: boolean;
  orientation?: DSDrawerOrientation;
  overlayClassName?: string;
  size?: DSDrawerSize;
  onClose?: () => void;
} & Pick<HTMLAttributes<HTMLDivElement>, "aria-label" | "aria-labelledby"> & {
    className?: string;
  };

const containerClassByOrientation: Record<DSDrawerOrientation, string> = {
  bottom: "flex items-end pb-0",
  left: "flex items-stretch p-0 pr-4",
  right: "flex items-stretch justify-end p-0 pl-4",
  top: "flex items-start pt-0",
};

const containerClassBySize: Record<DSDrawerSize, Partial<Record<DSDrawerOrientation, string>>> = {
  fit: {},
  full: {
    bottom: "flex items-end p-0",
    left: "flex items-stretch p-0",
    right: "flex items-stretch justify-end p-0",
    top: "flex items-start p-0",
  },
};

const panelClassByOrientation: Record<DSDrawerOrientation, string> = {
  bottom: "max-h-[calc(100vh-16px)] w-full rounded-t-[32px] rounded-b-none",
  left: "h-full w-full max-w-[512px] rounded-r-[32px] rounded-l-none",
  right: "h-full w-full max-w-[512px] rounded-l-[32px] rounded-r-none",
  top: "max-h-[calc(100vh-16px)] w-full rounded-b-[32px] rounded-t-none",
};

const panelClassBySize: Record<DSDrawerSize, Partial<Record<DSDrawerOrientation, string>>> = {
  fit: {},
  full: {
    bottom: "h-full max-h-none w-full rounded-none",
    left: "h-full w-full max-w-none rounded-none",
    right: "h-full w-full max-w-none rounded-none",
    top: "h-full max-h-none w-full rounded-none",
  },
};

function DSDrawer({
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  children,
  className,
  closeOnOverlayClick = true,
  open,
  orientation = "bottom",
  overlayClassName,
  size = "fit",
  onClose,
}: DSDrawerProps) {
  useOverlayLifecycle(open, onClose);

  return (
    <DSOverlayPortal open={open}>
      <DSOverlayRoot
        className={cn(
          containerClassByOrientation[orientation],
          containerClassBySize[size][orientation],
          overlayClassName
        )}
        closeOnOverlayClick={closeOnOverlayClick}
        onClose={onClose}
      >
        <div
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-modal="true"
          className={cn(
            "overflow-auto bg-body-light-mode-1 shadow-[0_24px_80px_oklch(0.2101_0.0318_264.66_/_0.18)] transition-transform duration-300 ease-out",
            panelClassByOrientation[orientation],
            panelClassBySize[size][orientation],
            className
          )}
          role="dialog"
        >
          {children}
        </div>
      </DSOverlayRoot>
    </DSOverlayPortal>
  );
}

export { DSDrawer };
export type { DSDrawerOrientation, DSDrawerProps, DSDrawerSize };
